import React, { useCallback, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { Box, Button, Card, Container, Grid, TextField, Typography } from '@mui/material';
import { DatePicker, LoadingButton } from '@mui/lab';
import { format } from 'date-fns';

import { cacheKeys, routes } from 'config';
import { LayoutContext, LocalizationContext } from 'contexts';
import { ReportType } from 'types/Report';
import { sanitizeDescription } from 'helpers/html';
import { addQuery } from 'helpers/query';
import { ReportStatus } from 'clients/reports/reportsClient.types';
import { reportsClient } from 'clients/reports/reportsClient';
import { useQueryState } from 'shared/hooks/useQueryState';
import { useReportTemplate } from 'shared/hooks/useReportTemplate';
import { BodyLoading } from 'shared/components/layout/BodyLoading/BodyLoading';
import { ReportQuestionList } from 'views/LoggedIn/Report/ReportQuestionList';


export const PreviewReportForm = () => {
  const [reportDate] = useQueryState('reportDate');
  const { templateId = null }: any = useParams();
  const { dictionary } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { genericError, genericFeedback } = useContext(LayoutContext);

  const methods = useForm<any>({
    defaultValues: {
      status: ReportStatus.Draft,
      report_template: Number(templateId),
      start_date: new Date(reportDate || ''),
    },
  });
  const { handleSubmit, watch, formState: { isSubmitting } } = methods;
  const { template, error, status } = useReportTemplate({ id: templateId });

  const queryClient = useQueryClient();
  const createReport = useMutation(reportsClient.createReport, {
    mutationKey: cacheKeys.reports.createReport,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries(cacheKeys.reports.getReports);
      queryClient.invalidateQueries(cacheKeys.tasks.getDropdownResults);
      queryClient.invalidateQueries(cacheKeys.dashboard.getDashboardMetadata);
      navigate(routes.fulfillReport(data.data.id));
      genericFeedback(dictionary.reports.form.reportCreated);
    },
    onError: () => {
      genericError();
    },
  });

  const onSubmit = useCallback((data: any) => {
    const mappedData = {
      report_template: data.report_template,
      start_date: format(data.start_date, 'yyyy-MM-dd'),
      category: ReportType.Report,
    };

    return createReport.mutateAsync(mappedData);
  }, [createReport]);

  if (status === 'loading') {
    return <BodyLoading height="40vh"/>;
  }

  if (error) {
    navigate(routes.reports);
    return null;
  }
  
  const startDate = watch('start_date');

  return (
    <Container maxWidth="md">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container direction="column" alignItems="center" sx={{ minHeight: 'calc(100vh - 100px)', mt: '100px' }}>
            <Grid item>
              <Card sx={{ p: 3, position: 'relative', backgroundColor: '#F7F7F7' }}>
                <Grid container>
                  <Grid item sm={10}>
                    <Typography variant="h4">{template?.title}</Typography>
                    {template?.description && (<Typography variant="body1" sx={{ mt: 2, mb: 3 }} dangerouslySetInnerHTML={{ __html: sanitizeDescription(template.description) }}/>)}
                  </Grid>
                  <Grid item sm={2}>
                    <Controller
                      name="start_date"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          inputFormat="dd.MM.yyyy"
                          mask="__.__.____"
                          label={dictionary.reports.form.fieldReportDate}
                          renderInput={props => <TextField size="small" {...props}/>}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} paddingY="30px">
                    <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
                      <ReportQuestionList report={template} isReadOnly/>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item alignItems="center" display="flex" justifyContent="space-between" width="100%" mt="30px">
              <Button
                variant="outlined"
                size="large"
                color="primary"
                type="button"
                onClick={() => navigate({
                  pathname: routes.createReport,
                  search: addQuery(location.search, {
                    reportDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
                    templateId: templateId,
                  }) })
                }
              >
                {dictionary.incidents.form.backButton}
              </Button>
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || !templateId || !startDate}
                loading={isSubmitting}
              >
                {dictionary.incidents.form.createButton}
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};
